import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * RADashboardPage — Org-wide AI-driven Risk Assessment dashboard.
 *
 * Shown to: bcm_manager, admin, department_head.
 * Auto-create RA tasks, view completion %, heatmap, top residual risks,
 * AI narrative (refresh on-demand).
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ShieldAlert, RefreshCw, Sparkles, AlertTriangle, CheckCircle2,
  TrendingUp, Zap, Loader2,
} from "lucide-react";

export default function RADashboardPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [aiRefreshing, setAiRefreshing] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);

  const fetchData = useCallback(async (refreshAi = false) => {
    try {
      setLoading(true);
      const res = await api.get(`/ra-workflow/dashboard${refreshAi ? "?refresh_ai=true" : ""}`);
      setDash(res.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setAiRefreshing(false);
    }
  }, [api]);

  useEffect(() => { fetchData(false); }, [fetchData]);

  const refreshAi = () => {
    setAiRefreshing(true);
    fetchData(true);
  };

  const autoCreate = async () => {
    setAutoCreating(true);
    try {
      const res = await api.post("/ra-workflow/auto-create", { scope: "all" });
      toast.success(
        `Created ${res.data.created} new RA task(s), skipped ${res.data.skipped} existing.`
      );
      fetchData(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Auto-create failed"));
    } finally {
      setAutoCreating(false);
    }
  };

  if (loading && !dash) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
      </div>
    );
  }
  if (!dash) return null;

  const { buckets, by_type, by_type_approved, completion_pct, top_residual,
    overdue_count, overdue_sample, ai_summary, total } = dash;

  return (
    <div className="space-y-6" data-testid="ra-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex gap-2 mb-2">
            <Badge className="bg-sky-100 text-sky-700">BCM Dashboard</Badge>
            <Badge variant="outline">ISO 22301 § 8.2.3</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Risk Assessment Insights</h1>
          <p className="text-slate-600 mt-1">
            Org-wide visibility with AI-generated narrative.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={autoCreate} disabled={autoCreating}
            data-testid="auto-create-btn">
            {autoCreating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Auto-create RA Tasks
          </Button>
          <Button variant="outline" onClick={() => fetchData(false)}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500">Total RAs</p>
            <p className="text-2xl font-bold">{total}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-4">
            <p className="text-xs text-emerald-700">Approved</p>
            <p className="text-2xl font-bold text-emerald-700">{buckets.approved || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-4">
            <p className="text-xs text-amber-700">Awaiting Review</p>
            <p className="text-2xl font-bold text-amber-700">{buckets.submitted || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-4">
            <p className="text-xs text-blue-700">In Progress</p>
            <p className="text-2xl font-bold text-blue-700">{buckets.in_progress || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="pt-4">
            <p className="text-xs text-red-700">Overdue</p>
            <p className="text-2xl font-bold text-red-700">{overdue_count}</p>
          </CardContent>
        </Card>
      </div>

      {/* Completion progress */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Org-wide RA Completion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">{buckets.approved || 0} of {total} approved</span>
            <span className="text-sm text-slate-500">{completion_pct}%</span>
          </div>
          <Progress value={completion_pct}
            className={`h-3 ${completion_pct >= 80 ? "[&>div]:bg-emerald-500"
              : completion_pct >= 50 ? "[&>div]:bg-amber-500" : "[&>div]:bg-red-500"}`} />
          <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
            {["process", "application", "it_function"].map((t) => (
              <div key={t} className="p-2 bg-slate-50 rounded">
                <p className="font-semibold capitalize">{t.replace("_", " ")}</p>
                <p className="text-slate-600">
                  {by_type_approved[t] || 0} / {by_type[t] || 0} approved
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Narrative */}
      <Card className="bg-gradient-to-r from-sky-50 to-teal-50 border-sky-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-5 h-5 text-sky-700" />
              AI Risk Insights
            </CardTitle>
            <Button size="sm" onClick={refreshAi} disabled={aiRefreshing}
              className="bg-sky-700 hover:bg-sky-800 text-white"
              data-testid="refresh-ai-btn">
              {aiRefreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Refresh AI
            </Button>
          </div>
          {ai_summary?._cached_at && (
            <CardDescription className="text-xs">
              Cached: {new Date(ai_summary._cached_at).toLocaleString()}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3" data-testid="ai-summary">
          <p className="text-slate-800">{ai_summary?.narrative}</p>
          {ai_summary?.top_concerns?.length > 0 && (
            <div>
              <p className="font-semibold text-sm text-slate-700 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4 text-amber-600" /> Top Concerns
              </p>
              <ul className="list-disc pl-6 text-sm text-slate-700">
                {ai_summary.top_concerns.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
          {ai_summary?.recommended_actions?.length > 0 && (
            <div>
              <p className="font-semibold text-sm text-slate-700 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Recommended Actions
              </p>
              <ul className="list-disc pl-6 text-sm text-slate-700">
                {ai_summary.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          <p className="text-[10px] text-slate-400">Source: {ai_summary?._source || "—"}</p>
        </CardContent>
      </Card>

      {/* Top Residual Risks */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="w-5 h-5 text-red-600" />
            Top 10 Residual Risks
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(top_residual || []).length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              No residual risks measured yet. Owners must complete RA forms with
              residual likelihood × impact.
            </div>
          ) : (
            <div className="divide-y">
              {top_residual.map((r, i) => (
                <div key={i} className="p-3 flex justify-between items-center"
                  data-testid={`residual-${i}`}>
                  <div>
                    <p className="font-medium">{r.asset_name}</p>
                    <p className="text-xs text-slate-500">
                      {r.asset_type} • Treatment: {r.treatment || "—"}
                    </p>
                  </div>
                  <Badge className={
                    r.residual_risk >= 15 ? "bg-red-100 text-red-700"
                      : r.residual_risk >= 8 ? "bg-amber-100 text-amber-700"
                      : "bg-blue-100 text-blue-700"
                  }>
                    {r.residual_risk}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overdue Sample */}
      {overdue_sample?.length > 0 && (
        <Card className="bg-red-50 border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-red-800">
              <ShieldAlert className="w-5 h-5" /> Overdue RAs ({overdue_count})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {overdue_sample.map((t) => (
              <div key={t.id} className="flex justify-between py-1" data-testid={`overdue-${t.id}`}>
                <span>{t.asset_name}</span>
                <span className="text-red-700 font-semibold">{t.days_open}d open</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
